// Utils
import clamp from '../../../common/maths/clamp';
import measurementsRegistry from '../../components/measurementsStore/measurementsRegistry';
import { gridPointsToPixels } from '../../utils/grid/gridUtils';
import { BOARD_PADDING_GRID_POINTS, getToolbarBoundingRect, unflattenCanvasLayers } from './canvasScaleCommonUtils';

// Constants
import { EXPORT_ROOT_ELEMENT_ID } from '../../workspace/export/exportConstants';
import { ELEMENT_DEFAULT_WIDTH } from '../../../common/elements/elementConstants';

/**
 * Step 1: Set the page classes to prepare for transition & returning to the normal mode.
 */
export const initialiseUnscale = () => {
    const workspacePositioner = document.querySelector('.WorkspacePositioner');
    // When zooming back in remove the scaled out class but add the zoom animation class again
    // Can't use replace because of electron :/
    workspacePositioner.classList.remove('scaled');
    workspacePositioner.classList.add('scale-animation');
};

export const setUnscaledTransitionDestinationStyles = ({ gridSize, returnCanvasScroll }) => {
    const scrollableContainer = document.getElementById(EXPORT_ROOT_ELEMENT_ID);
    const toolbarRect = getToolbarBoundingRect();
    const toolbarWidth = (toolbarRect && toolbarRect.width) || 0;

    const translationX = Math.round(toolbarWidth - returnCanvasScroll.left);
    const translationY = Math.round(-returnCanvasScroll.top);
    scrollableContainer.style.transform = `translate3d(${translationX}px, ${translationY}px, 0) scale(1)`;
};

export const cleanupOnUnscaleTransitionEnd = ({ returnCanvasScroll }) => {
    const workspacePositioner = document.querySelector('.WorkspacePositioner');
    workspacePositioner.classList.remove('scale-animation');

    const scrollableContainer = document.getElementById(EXPORT_ROOT_ELEMENT_ID);
    scrollableContainer.style.position = null;
    scrollableContainer.style.background = null;
    scrollableContainer.style.transition = null;
    scrollableContainer.style.transform = null;
    scrollableContainer.style.transformOrigin = null;

    if (returnCanvasScroll) {
        const canvasViewportNode = measurementsRegistry.getCanvasViewportDomElement();
        canvasViewportNode.scrollLeft = returnCanvasScroll.left;
        canvasViewportNode.scrollTop = returnCanvasScroll.top;
    }

    unflattenCanvasLayers();
};

export const getReturnCanvasScrollOnClick = (event, { scaleFactor = 1, elementBoundingRect, gridSize }) => {
    // Find the desired center point (where the click happened)
    const scrollableContainer = document.getElementById(EXPORT_ROOT_ELEMENT_ID);
    const scrollableContainerRect = scrollableContainer.getBoundingClientRect();

    const translatedClickPositionX = clamp(
        0,
        scrollableContainerRect.width,
        event.clientX - scrollableContainerRect.left,
    );
    const scaledClickPositionX = translatedClickPositionX / scaleFactor;

    const translatedClickPositionY = clamp(
        0,
        scrollableContainerRect.height,
        event.clientY - scrollableContainerRect.top,
    );
    const scaledClickPositionY = translatedClickPositionY / scaleFactor;

    // Translate to the center point of the canvas
    const canvasViewportNode = measurementsRegistry.getCanvasViewportDomElement();
    const canvasContainerRect = canvasViewportNode.getBoundingClientRect();

    const toolbarRect = getToolbarBoundingRect();
    const toolbarWidth = (toolbarRect && toolbarRect.width) || 0;

    const inboxDomNode = document.querySelector('.Inbox');
    const inboxRect = (inboxDomNode && inboxDomNode.getBoundingClientRect()) || null;
    const inboxWidth = (inboxRect && inboxRect.width) || 0;

    const unscaledCanvasWidth = canvasContainerRect.width - toolbarWidth - inboxWidth;
    const centerOfCanvasViewportX = unscaledCanvasWidth / 2;
    const centerOfCanvasViewportY = canvasContainerRect.height / 2;

    const boardPadding = gridPointsToPixels(BOARD_PADDING_GRID_POINTS, gridSize);

    // Add extra padding to the max X value if the inbox is closed.  This makes unscale feel better
    const boardPaddingX =
        inboxWidth === 0
            ? gridPointsToPixels(BOARD_PADDING_GRID_POINTS + ELEMENT_DEFAULT_WIDTH, gridSize)
            : boardPadding;

    const maxX = elementBoundingRect
        ? elementBoundingRect.left + elementBoundingRect.width + boardPaddingX
        : scrollableContainerRect.width / scaleFactor;
    const maxY = elementBoundingRect
        ? elementBoundingRect.top + elementBoundingRect.height + boardPadding
        : scrollableContainerRect.height / scaleFactor;

    // We want to scroll to the clicked position
    const returnScrollX = Math.round(
        clamp(0, Math.max(0, maxX - unscaledCanvasWidth), scaledClickPositionX - centerOfCanvasViewportX),
    );
    const returnScrollY = Math.round(
        clamp(0, Math.max(0, maxY - canvasContainerRect.height), scaledClickPositionY - centerOfCanvasViewportY),
    );

    return {
        left: returnScrollX,
        top: returnScrollY,
    };
};

export const immediatelyUnscale = () => {
    const workspacePositioner = document.querySelector('.WorkspacePositioner');
    workspacePositioner.classList.add('scaled-no-transition');
    workspacePositioner.classList.remove('scaled', 'scale-animation');

    const scrollableContainer = document.getElementById(EXPORT_ROOT_ELEMENT_ID);
    scrollableContainer.style.background = null;
    scrollableContainer.style.transition = null;
    scrollableContainer.style.transform = null;
    scrollableContainer.style.transformOrigin = null;

    unflattenCanvasLayers();

    requestAnimationFrame(() => {
        workspacePositioner.classList.remove('scaled-no-transition');
    });
};
